import { InternalRateChip } from "@/components/plans/InternalRateChip";
import { varianceTone } from "@/components/plans/PlanActualsSummary";
import { badgeVariants } from "@/components/ui/badge";
import {
  formatPlanDelta,
  formatPlanHours,
  formatPlanMoney,
  formatPlanPercent,
} from "@/lib/plan-format";
import { cn } from "@/lib/utils";
import type {
  ActualsFiguresView,
  ActualsRateSourceView,
  PlanActualsRowView,
  PlanActualsView,
} from "@/schemas/plan-actuals";

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function monthLabel(month: string): string {
  const [year, m] = month.split("-");
  return `${MONTH_NAMES[Number(m) - 1] ?? m} ${year?.slice(2) ?? ""}`;
}

const RATE_SOURCE_LABEL: Record<ActualsRateSourceView, { label: string; title: string } | null> = {
  line: null,
  "plan-category": {
    label: "Category rate",
    title:
      "Not on the plan. Priced at this plan's bill rate for the Labor Category their Pod Discipline maps to.",
  },
  "category-blended": {
    label: "Blended bill rate",
    title:
      "Not on the plan. Cost is their Pod Discipline's Labor Category; the plan doesn't price that category, so revenue uses the plan's blended bill rate.",
  },
  blended: {
    label: "Blended rates",
    title:
      "Not on the plan and no Labor Category could be inferred, so priced at the plan's blended bill rate and blended cost.",
  },
  unpriced: {
    label: "Unpriced",
    title: "The plan has no hours to blend a rate from, so these hours carry no revenue or cost.",
  },
};

function Num({ children, className }: { children: string; className?: string }) {
  return (
    <td className={cn("whitespace-nowrap px-3 py-2 text-right tabular-nums", className)}>
      {children}
    </td>
  );
}

/** The compared cells for one row — shared by lines, Not-on-plan Resources and the total. */
function FigureCells({ f, showCost }: { f: ActualsFiguresView; showCost: boolean }) {
  return (
    <>
      <Num>{formatPlanHours(f.plannedHours)}</Num>
      <Num>{formatPlanHours(f.actualHours)}</Num>
      <Num>
        {`${formatPlanDelta(f.hoursVariance)} · ${formatPlanPercent(f.hoursVariancePercent, { signed: true })}`}
      </Num>
      <Num>{formatPlanMoney(f.plannedRevenue)}</Num>
      <Num>{formatPlanMoney(f.actualRevenue)}</Num>
      {showCost && (
        <>
          <Num>{formatPlanMoney(f.plannedCost)}</Num>
          <Num>{formatPlanMoney(f.actualCost)}</Num>
          <Num className={varianceTone(f.costVariance, "bad")}>
            {formatPlanDelta(f.costVariance, "money")}
          </Num>
        </>
      )}
      <Num>{formatPlanPercent(f.plannedMarginPercent)}</Num>
      <Num>{formatPlanPercent(f.actualMarginPercent)}</Num>
      <Num className={varianceTone(f.marginVariancePoints, "good")}>
        {formatPlanPercent(f.marginVariancePoints, { signed: true, unit: "pts" })}
      </Num>
    </>
  );
}

function RowLabel({ row }: { row: PlanActualsRowView }) {
  const source = RATE_SOURCE_LABEL[row.rateSource];
  return (
    <>
      <td className="px-3 py-2">{row.laborCategoryName ?? "—"}</td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className={cn(row.resourceName === null && "text-muted-foreground")}>
            {row.resourceName ?? (row.kind === "line" ? "Open" : "Unknown Resource")}
          </span>
          {source !== null && (
            <span
              className={cn(badgeVariants({ variant: "secondary" }), "cursor-help")}
              title={source.title}
            >
              {source.label}
            </span>
          )}
          {/* Priced at this Resource's own Internal Rate: the server sent no cost or
              margin for the row, and this says why the cells are empty. */}
          {row.internalRateSource === "resource" && row.costRate === undefined && (
            <InternalRateChip />
          )}
        </div>
      </td>
    </>
  );
}

/**
 * The detail level (spec R3, AC2, AC3, AC5): one row per Plan Line, then every Resource
 * who logged time on the Solution without being on the plan, then the plan total.
 */
export function PlanActualsTable({ view }: { view: PlanActualsView }) {
  const showCost = view.canViewCost;
  const onPlan = view.rows.filter((r) => r.kind === "line");
  const notOnPlan = view.rows.filter((r) => r.kind === "unplanned");
  const columnCount = 2 + 3 + 2 + (showCost ? 3 : 0) + 3;

  const head = (label: string, title?: string) => (
    <th
      className={cn("px-3 py-2 text-right font-medium", title !== undefined && "cursor-help")}
      title={title}
    >
      {label}
    </th>
  );

  return (
    <div className="overflow-x-auto rounded-md border border-border bg-card">
      <table className="w-full min-w-[1100px] text-sm">
        <thead className="border-b border-border text-xs text-muted-foreground">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Labor Category</th>
            <th className="px-3 py-2 text-left font-medium">Resource</th>
            {head("Planned h")}
            {head("Actual h", "All hours logged in NOS, billable or not.")}
            {head("Variance h")}
            {head("Planned revenue")}
            {head("Actual revenue", "Billable hours × bill rate.")}
            {showCost && (
              <>
                {head("Planned cost")}
                {head("Actual cost", "All hours × the Labor Category's internal cost.")}
                {head("Variance cost")}
              </>
            )}
            {head(
              "Planned margin",
              "Indicative — at the plan's bill rates, before any Contracted Fee or discount.",
            )}
            {head("Actual margin")}
            {head("Δ margin", "Actual margin − planned margin, in percentage points.")}
          </tr>
        </thead>
        <tbody>
          <tr className="bg-muted/40">
            <td
              colSpan={columnCount}
              className="px-3 py-1.5 text-xs font-semibold text-muted-foreground"
            >
              On plan
            </td>
          </tr>
          {onPlan.length === 0 ? (
            <tr>
              <td colSpan={columnCount} className="px-3 py-2 text-muted-foreground">
                This plan has no Plan Lines.
              </td>
            </tr>
          ) : (
            onPlan.map((row) => (
              <tr key={row.key} className="border-b border-border last:border-b-0">
                <RowLabel row={row} />
                <FigureCells f={row} showCost={showCost} />
              </tr>
            ))
          )}
          {notOnPlan.length > 0 && (
            <>
              <tr className="bg-muted/40">
                <td
                  colSpan={columnCount}
                  className="px-3 py-1.5 text-xs font-semibold text-muted-foreground"
                  title="Resources who logged time on this Solution in NOS but are on no Plan Line."
                >
                  Not on plan
                </td>
              </tr>
              {notOnPlan.map((row) => (
                <tr key={row.key} className="border-b border-border last:border-b-0">
                  <RowLabel row={row} />
                  <FigureCells f={row} showCost={showCost} />
                </tr>
              ))}
            </>
          )}
        </tbody>
        <tfoot className="border-t border-border font-semibold">
          <tr>
            <td className="px-3 py-2" colSpan={2}>
              Plan total to date
            </td>
            <FigureCells f={view.summary} showCost={showCost} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/** Planned vs actual per month across the compared window. */
export function PlanActualsByMonth({ view }: { view: PlanActualsView }) {
  const lastMonth = view.windowMonths[view.windowMonths.length - 1];
  const rows: Array<{
    label: string;
    cell: (m: PlanActualsView["byMonth"][number]) => string;
    tone?: (m: PlanActualsView["byMonth"][number]) => string | undefined;
  }> = [
    { label: "Planned h", cell: (m) => formatPlanHours(m.plannedHours) },
    { label: "Actual h", cell: (m) => formatPlanHours(m.actualHours) },
    {
      label: "Variance h",
      cell: (m) => formatPlanDelta(m.hoursVariance),
    },
    { label: "Planned margin", cell: (m) => formatPlanPercent(m.plannedMarginPercent) },
    { label: "Actual margin", cell: (m) => formatPlanPercent(m.actualMarginPercent) },
  ];

  return (
    <div className="overflow-x-auto rounded-md border border-border bg-card">
      <table className="w-full text-sm">
        <thead className="border-b border-border text-xs text-muted-foreground">
          <tr>
            <th className="px-3 py-2 text-left font-medium">By month</th>
            {view.byMonth.map((m) => (
              <th key={m.month} className="px-3 py-2 text-right font-medium">
                {monthLabel(m.month)}
                {view.includesPartialMonth && m.month === lastMonth && (
                  <span className="block font-normal">to date</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label} className="border-b border-border last:border-b-0">
              <td className="px-3 py-2 text-muted-foreground">{r.label}</td>
              {view.byMonth.map((m) => (
                <Num key={m.month} className={r.tone?.(m)}>
                  {r.cell(m)}
                </Num>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
